import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import textToSpeechIcon from '@/assets/images/text_to_speech_icon.svg';
import useContactStore from '@/stores/ContactStore';
import useInboxStore from '@/stores/InboxStore';
import useUserStore from '@/stores/UserStore';
import { useHotkeys } from '@blueprintjs/core';
import {
  AudioLines,
  ChevronDownIcon,
  MailIcon,
  MessageSquareIcon,
  SendHorizonalIcon,
  VideoIcon,
  XIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import {
  trackContactStatusChange,
  trackMessageInputClicked,
  trackQuickCloseContactFeature,
} from '@/lib/analytics-event';
import { cn, isMergedChannel } from '@/lib/utils';
import { updateContactStatus } from '@/hooks/contact';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';

import AudioRecorder from './Message/AudioRecorder';
import CannedMessagesPopover from './Message/CannedMessagePopover';
import CannedMessageSuggestions from './Message/CannedMessageSuggestions';
import EmojiPicker from './Message/EmojiPicker';
import FileAttachment from './Message/FileAttachment';
import PersonalLinkButton from './Message/PersonalLinkButton';
import TextToSpeech from './Message/TextToSpeech';

export default function MessageInput({ addMessage }) {
  const [messageType, setMessageType] = useState('text');
  const [message, setMessage] = useState('');
  const [isAttachmentSelected, setIsAttachmentSelected] = useState(false);
  const [isCannedMessagePopoverOpen, setIsCannedMessagePopoverOpen] =
    useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isAudioRecorderActive, setIsAudioRecorderActive] = useState(false);
  const [isTextToSpeechActive, setIsTextToSpeechActive] = useState(false);
  // For merged channels: 'sms' or 'email' message type selector
  const [mergedChannelMode, setMergedChannelMode] = useState('sms');

  const textareaRef = useRef(null);

  const navigate = useNavigate();

  const clientId = useUserStore((state) => state.clientId);
  const {
    conversations,
    removeConversation,
    increaseConversationCount,
    decreaseConversationCount,
    selectedConversationStatus,
  } = useInboxStore(
    useShallow((state) => ({
      conversations: state.conversations,
      removeConversation: state.removeConversation,
      increaseConversationCount: state.increaseConversationCount,
      decreaseConversationCount: state.decreaseConversationCount,
      selectedConversationStatus: state.selectedConversationStatus,
    }))
  );

  const {
    id,
    status,
    channel,
    contactUsername,
    contactFullname,
    owner,
    selectedPrivateReplyMessage,
    selectedPrivateReplyMessageId,
    resetSelectedPrivateReplyMessage,
    messageToTweak,
    resetMessageToTweak,
    selectedEmailThreadForReply,
    resetSelectedEmailThreadForReply,
    selectedReplyToMessage,
    resetSelectedReplyToMessage,
  } = useContactStore(
    useShallow((state) => ({
      id: state._id,
      status: state.status,
      channel: state.channel,
      contactUsername: state.ig_username,
      contactFullname: state.fullname,
      owner: state.owner,
      selectedPrivateReplyMessage: state.selectedPrivateReplyMessage,
      selectedPrivateReplyMessageId: state.selectedPrivateReplyMessageId,
      resetSelectedPrivateReplyMessage: state.resetSelectedPrivateReplyMessage,
      messageToTweak: state.messageToTweak,
      resetMessageToTweak: state.resetMessageToTweak,
      selectedEmailThreadForReply: state.selectedEmailThreadForReply,
      resetSelectedEmailThreadForReply: state.resetSelectedEmailThreadForReply,
      selectedReplyToMessage: state.selectedReplyToMessage,
      resetSelectedReplyToMessage: state.resetSelectedReplyToMessage,
    }))
  );

  const { shortcuts } = useUserStore(
    useShallow((state) => ({
      shortcuts: state.cannedResponses,
    }))
  );

  const filteredShortcuts = useMemo(() => {
    return shortcuts.filter((shortcut) => !shortcut.overridden);
  }, [shortcuts]);

  const recordingFeatureEnabled = useUserStore(
    (state) => state.recordingFeatureEnabled
  );

  // Effect to handle messageToTweak changes
  useEffect(() => {
    if (messageToTweak) {
      setMessage(messageToTweak);
      resetMessageToTweak();
      // Focus the textarea and move cursor to end
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = messageToTweak.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }
  }, [messageToTweak, resetMessageToTweak]);

  // Effect to auto-switch to email mode when an email thread is selected for merged channels
  useEffect(() => {
    if (selectedEmailThreadForReply && isMergedChannel(channel)) {
      setMergedChannelMode('email');
    }
  }, [selectedEmailThreadForReply, channel]);

  const handleMessage = async (e) => {
    e.preventDefault();
    //if text value is not emptry then call onaddMessage function
    if (
      messageType === 'text' &&
      message !== '' &&
      selectedPrivateReplyMessageId === null &&
      selectedEmailThreadForReply === null &&
      selectedReplyToMessage === null
    ) {
      // For merged channel with SMS mode selected
      if (isMergedChannel(channel) && mergedChannelMode === 'sms') {
        setMessage('');
        return await addMessage(message, 'text');
      }
      // Normal Text Message
      setMessage('');
      return await addMessage(message, 'text');
    } else if (
      messageType === 'text' &&
      message !== '' &&
      selectedPrivateReplyMessageId !== null
    ) {
      // Private DM for Comment
      setMessage('');
      resetSelectedPrivateReplyMessage();
      const options = {
        sbcMessageId: selectedPrivateReplyMessageId,
        commentText: selectedPrivateReplyMessage,
      };
      return await addMessage(message, 'private_reply_comment', options);
    } else if (
      messageType === 'text' &&
      message !== '' &&
      selectedEmailThreadForReply !== null
    ) {
      // Email Thread Reply
      setMessage('');
      const threadId = selectedEmailThreadForReply.thread_id;
      resetSelectedEmailThreadForReply();
      const options = {
        threadId: threadId,
      };
      return await addMessage(message, 'email_reply', options);
    } else if (
      messageType === 'text' &&
      message !== '' &&
      selectedReplyToMessage !== null
    ) {
      // Reply to Message (Instagram)
      setMessage('');
      const replyToMessageId = selectedReplyToMessage._id;
      const replyToMessage = selectedReplyToMessage;
      resetSelectedReplyToMessage();
      const options = {
        replyToMessageId: replyToMessageId,
        replyToMessage: replyToMessage,
      };
      return await addMessage(message, 'reply_to_message', options);
    }
  };

  const handleRecordingComplete = async (audioBlob) => {
    try {
      await addMessage(audioBlob, 'audio');
      setIsRecording(false);
    } catch (error) {
      toast.error('Failed to send audio message');
      setIsRecording(false);
      throw error;
    }
  };

  const handleRecordingCancel = () => {
    setIsRecording(false);
  };

  const handleTextToSpeechComplete = async (audioFile) => {
    try {
      await addMessage(audioFile, 'audio');
      setMessage(''); // Clear the message after successful send
      setIsTextToSpeechActive(false);
    } catch (error) {
      toast.error('Failed to send audio message');
      setIsTextToSpeechActive(false);
      throw error;
    }
  };

  const handleTextToSpeechCancel = () => {
    setIsTextToSpeechActive(false);
  };

  const handleStartTextToSpeech = () => {
    if (!message || !message.trim()) {
      toast.error('Please enter text to convert to speech');
      return;
    }
    setIsTextToSpeechActive(true);
  };

  const updateStatus = async () => {
    try {
      const newStatus = status === 'open' ? 'close' : 'open';
      const response = await updateContactStatus(id, newStatus, clientId);
      trackContactStatusChange(newStatus);

      return response;
    } catch (error) {
      toast.dismiss('sending-message');
      toast.error('Unable to update contact status');
    }
  };

  const processMessageContent = (content) => {
    let result = '';

    content.forEach((item) => {
      if (item.type === 'text') {
        result += item.text;
      } else if (item.type === 'mention') {
        if (item.attrs.id === 'first-name') {
          result += contactFullname?.split(' ')[0] || '';
        }
      } else if (item.type === 'hardBreak') {
        result += '\n';
      }
    });

    return result;
  };

  const processMessage = (message) => {
    let result = '';

    message.content.forEach((paragraph) => {
      if (paragraph.content) {
        result += processMessageContent(paragraph.content);
      }
      // Add newline between paragraphs if it's not the last paragraph
      if (paragraph !== message.content[message.content.length - 1]) {
        result += '\n';
      }
    });

    return result;
  };

  const handleMessageChange = (e) => {
    const newValue = e.target.value;
    const lastChar = newValue.slice(-1);
    setMessage(newValue);

    // Only process shortcuts if the last character typed was a space
    if (lastChar === ' ') {
      // Check if the text before the space matches any shortcut
      const textBeforeSpace = newValue.slice(0, -1);
      const matchedShortcut = filteredShortcuts.find((item) =>
        textBeforeSpace.endsWith(item.shortcut)
      );

      if (matchedShortcut) {
        const processedMessage = processMessage(matchedShortcut.message);
        const newMessage =
          newValue.slice(0, -matchedShortcut.shortcut.length - 1) +
          processedMessage +
          ' ';
        setMessage(newMessage);

        // Set cursor position to end of processed text
        setTimeout(() => {
          e.target.selectionStart = newMessage.length;
          e.target.selectionEnd = newMessage.length;
        }, 0);
      }
    }
  };

  const handleSuggestionSelect = (newText) => {
    setMessage(newText);

    // Focus the textarea and move cursor to end
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = newText.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, 0);
  };

  const handleKeyDown = async (e) => {
    // Add CTRL+M handler for textarea, but don't prevent default
    if ((e.ctrlKey || e.metaKey) && e.key === 'm') {
      setIsCannedMessagePopoverOpen(true);
      return;
    }

    // If ctrl + enter is pressed, send message and remove focus from textarea
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      try {
        toast.loading('Loading...', {
          id: 'sending-message',
        });

        trackQuickCloseContactFeature();

        const response = await handleMessage(e);
        textareaRef?.current.blur();

        const newStatus = status === 'open' ? 'close' : 'open';

        // If status is open, update status to close
        const updateStatusResponse = await updateStatus();

        if (response?.status === 'success' && updateStatusResponse) {
          toast.dismiss('sending-message');
          toast.success('Message sent and status updated');
          if (selectedConversationStatus.value !== 'all') {
            switch (selectedConversationStatus.value) {
              // If the contact status is changed to the selectedConversationStatus
              case newStatus:
                console.log('insert conversation triggered', id);
                // addConversationToTop(contactData);
                increaseConversationCount();
                break;

              // If the contact status is changed to the opposite of selectedConversationStatus
              default:
                console.log('remove conversation triggered', id);
                removeConversation(id);
                decreaseConversationCount();
                break;
            }
          }

          // Find id index in conversations array
          const index = conversations.findIndex(
            (conversation) => conversation._id === id
          );

          // If index is found, navigate to next conversation
          // If index (-1) is not found, do not navigate
          // if index is last element, do not navigate

          if (index !== -1 && index !== conversations.length - 1) {
            navigate(`/inbox/${conversations[index + 1]._id}`, {
              replace: true,
            });
          } else {
            navigate(`/inbox`, {
              replace: true,
            });
          }
        }
      } catch (error) {
        toast.dismiss('sending-message');
        toast.error('Unable to send message');
      }
    } else if (e.key === 'Enter') {
      // If Shift key is also pressed, insert a newline character
      if (e.shiftKey) {
        // dont do anything
      } else {
        // Otherwise, prevent the default behavior (submitting the form) and perform your button click action
        e.preventDefault();
        handleMessage(e);
      }
    }

    // If esc is pressed, remove focus from textarea
    if (e.key === 'Escape') {
      textareaRef?.current.blur();
    }
  };

  const handleKeyPress = useCallback(() => {
    textareaRef?.current.focus();
    trackMessageInputClicked('keyboard');
  }, []);

  const handleOpenCannedMessages = useCallback((e) => {
    // Prevent default only for the global hotkey to avoid double-triggering
    if (e) e.preventDefault();
    setIsCannedMessagePopoverOpen(true);
  }, []);

  const handleStartRecording = useCallback(
    (e) => {
      // Prevent default only for the global hotkey to avoid double-triggering
      if (e) e.preventDefault();
      if (!isRecording && channel === 'instagram') {
        // Trigger recording start
        setIsRecording(true);
      }
    },
    [isRecording, channel]
  );

  const hotkeys = useMemo(
    () => [
      {
        combo: 'R',
        global: true,
        group: 'Contact Chat',
        // disabled: !contactSidebarShow,
        label: 'Reply',
        onKeyUp: handleKeyPress,
      },
      {
        combo: 'alt+s',
        global: true, // Changed back to true for global accessibility
        group: 'Contact Chat',
        label: 'Open Saved Replies',
        onKeyDown: handleOpenCannedMessages, // Changed to onKeyDown and will handle preventDefault
      },
      {
        combo: 'alt+r',
        global: true,
        group: 'Contact Chat',
        label: 'Start Audio Recording',
        onKeyDown: handleStartRecording,
      },
    ],
    [handleKeyPress, handleOpenCannedMessages, handleStartRecording]
  );
  const hotkey = useHotkeys(hotkeys);

  return (
    <>
      <div className='m-1 rounded-md'>
        <div className='m-1 mb-2 flex flex-col'>
          <div className='relative w-full'>
            {selectedPrivateReplyMessageId && (
              <div className='flex w-full flex-row items-center justify-between rounded-t-lg border-[2px] bg-background px-3 py-2 text-sm'>
                <div className='overflow-hidden text-ellipsis whitespace-nowrap'>
                  <span className='font-extralight text-primary'>
                    Private reply to Comment{' '}
                  </span>
                  <span className='font-medium'>
                    {selectedPrivateReplyMessage}
                  </span>
                </div>
                <XIcon
                  className='ml-2 h-5 w-5 flex-shrink-0 cursor-pointer'
                  onClick={() => resetSelectedPrivateReplyMessage()}
                />
              </div>
            )}

            {selectedReplyToMessage && (
              <div className='flex w-full flex-row items-center justify-between rounded-t-lg border-[2px] bg-background px-3 py-2 text-sm'>
                <div className='flex min-w-0 flex-1 items-center gap-2'>
                  <span className='flex-shrink-0 font-extralight text-primary'>
                    Replying to:{' '}
                  </span>
                  {/* Show media thumbnail if it's a media message */}
                  {selectedReplyToMessage.media_url &&
                    selectedReplyToMessage.message_type === 'image' && (
                      <img
                        src={selectedReplyToMessage.media_url}
                        alt='Reply media'
                        className='h-8 w-8 flex-shrink-0 rounded object-cover'
                      />
                    )}
                  {selectedReplyToMessage.media_url &&
                    selectedReplyToMessage.message_type === 'video' && (
                      <VideoIcon className='h-5 w-5 flex-shrink-0 text-muted-foreground' />
                    )}
                  {selectedReplyToMessage.media_url &&
                    selectedReplyToMessage.message_type === 'audio' && (
                      <AudioLines className='h-5 w-5 flex-shrink-0 text-muted-foreground' />
                    )}
                  <span className='max-w-[500px] truncate font-medium'>
                    {selectedReplyToMessage.text ||
                      (selectedReplyToMessage.message_type === 'image'
                        ? 'Photo'
                        : selectedReplyToMessage.message_type === 'video'
                          ? 'Video'
                          : selectedReplyToMessage.message_type === 'audio'
                            ? 'Voice message'
                            : 'Message')}
                  </span>
                </div>
                <XIcon
                  className='ml-2 h-5 w-5 flex-shrink-0 cursor-pointer'
                  onClick={() => resetSelectedReplyToMessage()}
                />
              </div>
            )}

            {selectedEmailThreadForReply && (
              <div className='flex w-full flex-row items-center justify-between rounded-t-lg border-[2px] bg-background px-3 py-2 text-sm'>
                <div className='flex min-w-0 flex-1 items-center'>
                  <span className='flex-shrink-0 font-extralight text-primary'>
                    Reply to Email Thread:{' '}
                  </span>
                  <span className='max-w-[200px] truncate font-semibold'>
                    {selectedEmailThreadForReply.subject || 'No Subject'}
                  </span>
                  {selectedEmailThreadForReply.preview && (
                    <span className='ml-2 max-w-[300px] truncate font-normal text-muted-foreground'>
                      - {selectedEmailThreadForReply.preview}
                    </span>
                  )}
                </div>
                <XIcon
                  className='ml-2 h-5 w-5 flex-shrink-0 cursor-pointer'
                  onClick={() => resetSelectedEmailThreadForReply()}
                />
              </div>
            )}

            <Textarea
              ref={textareaRef}
              rows='1'
              className={cn(
                'custom-scrollbar relative min-h-14 resize-none overflow-y-auto rounded-none border-0 bg-muted transition-all duration-300 ease-in-out focus-visible:ring-0 focus-visible:ring-offset-0',
                isAttachmentSelected ||
                  isAudioRecorderActive ||
                  isTextToSpeechActive
                  ? 'max-h-0 min-h-0 opacity-0'
                  : 'max-h-40 opacity-100',
                selectedPrivateReplyMessageId ||
                  selectedEmailThreadForReply ||
                  selectedReplyToMessage
                  ? ''
                  : 'rounded-t-lg'
              )}
              onInput={(e) => {
                const target = e.target;
                target.style.height = 'auto';
                target.style.height = `${target.scrollHeight}px`;
              }}
              value={message}
              onChange={handleMessageChange}
              onClick={() => trackMessageInputClicked('mouse')}
              placeholder={
                channel === 'email' && !selectedEmailThreadForReply
                  ? 'Select an email thread to reply'
                  : isMergedChannel(channel) &&
                      mergedChannelMode === 'email' &&
                      !selectedEmailThreadForReply
                    ? 'Select an email thread to reply'
                    : 'To type a message, press R. To add a new line, press Shift + Enter.'
              }
              onKeyDown={handleKeyDown}
              disabled={
                (channel === 'email' && !selectedEmailThreadForReply) ||
                (isMergedChannel(channel) &&
                  mergedChannelMode === 'email' &&
                  !selectedEmailThreadForReply)
              }
            />

            {/* Canned Message Suggestions Popup */}
            <CannedMessageSuggestions
              inputText={message}
              shortcuts={filteredShortcuts}
              onSelect={handleSuggestionSelect}
              processMessage={processMessage}
            />
          </div>
          {isTextToSpeechActive ? (
            <TextToSpeech
              text={message}
              onComplete={handleTextToSpeechComplete}
              onCancel={handleTextToSpeechCancel}
              setIsTextToSpeechActive={setIsTextToSpeechActive}
            />
          ) : (
            <div
              className={cn(
                'relative flex items-center justify-between bg-muted px-2 transition-all duration-300 ease-in-out',
                messageType === 'text' && !isAudioRecorderActive
                  ? 'rounded-b-lg pb-2'
                  : 'rounded-lg py-1',
                isAudioRecorderActive && 'p-0'
              )}
            >
              <div
                className={cn(
                  'flex w-full items-center justify-start',
                  !isAudioRecorderActive && 'gap-2'
                )}
              >
                {!isAudioRecorderActive && messageType === 'text' && (
                  <EmojiPicker
                    onEmojiSelect={(emoji) => {
                      setMessage((prev) => prev + emoji);
                      setTimeout(() => {
                        textareaRef.current?.focus();
                        const len = textareaRef.current?.value.length;
                        textareaRef.current?.setSelectionRange(len, len);
                      }, 0);
                    }}
                  />
                )}
                {!isAudioRecorderActive && messageType === 'text' && (
                  <CannedMessagesPopover
                    shortcuts={filteredShortcuts}
                    onSelect={(selectedText) => {
                      setMessage((prev) => prev + selectedText);
                      setTimeout(() => {
                        textareaRef.current?.focus();
                        const len = textareaRef.current?.value.length;
                        textareaRef.current?.setSelectionRange(len, len);
                      }, 0);
                    }}
                    processMessage={processMessage}
                    open={isCannedMessagePopoverOpen}
                    setOpen={setIsCannedMessagePopoverOpen}
                    textareaRef={textareaRef}
                  />
                )}
                {!isAudioRecorderActive && messageType === 'text' && (
                  <PersonalLinkButton
                    setMessage={setMessage}
                    textareaRef={textareaRef}
                    channel={channel}
                    contactUsername={contactUsername}
                    ownerId={owner}
                  />
                )}
                {!isAudioRecorderActive && channel === 'instagram' && (
                  <FileAttachment
                    setIsAttachmentSelected={setIsAttachmentSelected}
                    messageType={messageType}
                    setMessageType={setMessageType}
                    addMessage={addMessage}
                  />
                )}
                {!isAudioRecorderActive &&
                  !isAttachmentSelected &&
                  channel === 'instagram' && (
                    <Button
                      size='xs'
                      variant='ghost'
                      onClick={handleStartTextToSpeech}
                      className='text-primary transition-all hover:text-primary/90'
                      title='Convert text to speech'
                    >
                      <img
                        src={textToSpeechIcon}
                        alt='Text to Speech'
                        className='h-5 w-5 text-white'
                      />
                    </Button>
                  )}
                {!isAttachmentSelected &&
                  channel === 'instagram' &&
                  recordingFeatureEnabled && (
                    <AudioRecorder
                      onRecordingComplete={handleRecordingComplete}
                      onCancel={handleRecordingCancel}
                      isRecording={isRecording}
                      setIsRecording={setIsRecording}
                      setIsAudioRecorderActive={setIsAudioRecorderActive}
                    />
                  )}
              </div>
              {!isAudioRecorderActive && messageType === 'text' && (
                <div className='flex items-center gap-1'>
                  {/* SMS/Email Mode Selector for merged channels */}
                  {isMergedChannel(channel) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size='xs'
                          variant='outline'
                          className='flex items-center gap-1 text-xs'
                        >
                          {mergedChannelMode === 'sms' ? (
                            <>
                              <MessageSquareIcon className='h-3 w-3' />
                              SMS
                            </>
                          ) : (
                            <>
                              <MailIcon className='h-3 w-3' />
                              Email
                            </>
                          )}
                          <ChevronDownIcon className='h-3 w-3' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        <DropdownMenuItem
                          onClick={() => {
                            setMergedChannelMode('sms');
                            resetSelectedEmailThreadForReply();
                          }}
                          className={cn(
                            'flex items-center gap-2',
                            mergedChannelMode === 'sms' && 'bg-accent'
                          )}
                        >
                          <MessageSquareIcon className='h-4 w-4' />
                          SMS
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setMergedChannelMode('email')}
                          className={cn(
                            'flex items-center gap-2',
                            mergedChannelMode === 'email' && 'bg-accent'
                          )}
                        >
                          <MailIcon className='h-4 w-4' />
                          Email
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Button
                    size='xs'
                    variant='ghost'
                    className='text-primary hover:text-primary/90'
                    onClick={(e) => handleMessage(e)}
                    disabled={
                      (channel === 'email' && !selectedEmailThreadForReply) ||
                      (isMergedChannel(channel) &&
                        mergedChannelMode === 'email' &&
                        !selectedEmailThreadForReply)
                    }
                  >
                    <SendHorizonalIcon />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
